import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogService } from '../../services/firebaseService';
import { BlogPost } from '../../types';
import MarkdownEditor from '../../components/admin/MarkdownEditor';
import { Timestamp } from 'firebase/firestore';

const BlogManager: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Partial<BlogPost>>({ 
    title: '', 
    content: '', 
    excerpt: '',
    author: '',
    isPublished: true,
    publishedAt: Timestamp.now()
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    blogService.getAllBlogPosts().then(list => {
      setPosts(list);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingId) {
        await blogService.updateBlogPost(editingId, form as Partial<BlogPost>);
      } else {
        await blogService.createBlogPost(form as BlogPost);
      }
      const list = await blogService.getAllBlogPosts();
      setPosts(list);
      setForm({ 
        title: '', 
        content: '', 
        excerpt: '',
        author: '',
        isPublished: true,
        publishedAt: Timestamp.now()
      });
      setEditingId(null);
    } catch (error: any) {
      console.error('Error saving blog post:', error);
      const errorMessage = error?.code === 'permission-denied' 
        ? 'Permission denied. Please ensure Firestore rules for blogPosts are deployed. Check the browser console for details.'
        : error?.message || 'Failed to save blog post';
      alert(errorMessage);
    }
  }

  function startEdit(post: BlogPost) {
    setEditingId(post.id!);
    setForm({ 
      title: post.title, 
      content: post.content,
      excerpt: post.excerpt || '',
      author: post.author || '',
      isPublished: post.isPublished !== false,
      publishedAt: post.publishedAt || Timestamp.now()
    });
  }

  async function handleDelete(id: string) {
    try {
      await blogService.deleteBlogPost(id);
      const list = await blogService.getAllBlogPosts();
      setPosts(list);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post');
    }
  }

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return 'Not set';
    let date: Date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp as any);
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3>Admin Panel</h3>
        <nav>
          <ul>
            <li><Link to="/admin">Dashboard</Link></li>
            <li><Link to="/admin/content">Content</Link></li>
            <li><Link to="/admin/board">Board Members</Link></li>
            <li><Link to="/admin/programs">Programs</Link></li>
            <li><Link to="/admin/events">Events</Link></li>
            <li><Link to="/admin/blog">Blog Posts</Link></li>
            <li><Link to="/admin/volunteers">Volunteer Applications</Link></li>
            <li><Link to="/admin/contact">Contact Submissions</Link></li>
          </ul>
        </nav>
      </div>
      <div className="admin-content">
        <h1>Blog Manager</h1>

        <form onSubmit={handleSubmit} style={{ marginBottom: '2rem' }} className="card">
          <div className="grid" style={{ gridTemplateColumns: '1fr' }}>
            <textarea
              placeholder="Title"
              value={form.title || ''}
              onChange={e => setForm({ ...form, title: e.target.value })}
              rows={2}
              required
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 'inherit' }}
            />
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Excerpt (optional)</label>
              <textarea
                placeholder="Brief excerpt for blog listing..."
                value={form.excerpt || ''}
                onChange={e => setForm({ ...form, excerpt: e.target.value })}
                rows={3}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <input
              type="text"
              placeholder="Author (optional)"
              value={form.author || ''}
              onChange={e => setForm({ ...form, author: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 'inherit' }}
            />
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Content</label>
              <MarkdownEditor
                value={form.content || ''}
                onChange={(value) => setForm({ ...form, content: value })}
                placeholder="Enter blog post content..."
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Published Date</label>
              <input
                type="date"
                value={form.publishedAt instanceof Timestamp 
                  ? form.publishedAt.toDate().toISOString().split('T')[0]
                  : form.publishedAt instanceof Date
                  ? form.publishedAt.toISOString().split('T')[0]
                  : new Date().toISOString().split('T')[0]}
                onChange={e => {
                  const date = new Date(e.target.value);
                  setForm({ ...form, publishedAt: Timestamp.fromDate(date) });
                }}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.isPublished !== false}
                onChange={e => setForm({ ...form, isPublished: e.target.checked })}
              />
              Published
            </label>
            <div>
              <button className="btn btn-primary" type="submit">{editingId ? 'Update' : 'Create'} Post</button>
              {editingId && (
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  style={{ marginLeft: '0.5rem' }} 
                  onClick={() => { 
                    setEditingId(null); 
                    setForm({ 
                      title: '', 
                      content: '', 
                      excerpt: '',
                      author: '',
                      isPublished: true,
                      publishedAt: Timestamp.now()
                    }); 
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: '1fr' }}>
            {posts.map(post => (
              <div key={post.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ whiteSpace: 'pre-line' }}>{post.title}</h3>
                    <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      Published: {formatDate(post.publishedAt)}
                    </p>
                    <span style={{ 
                      display: 'inline-block',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      backgroundColor: post.isPublished !== false ? '#d4edda' : '#f8d7da',
                      color: post.isPublished !== false ? '#155724' : '#721c24'
                    }}>
                      {post.isPublished !== false ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                {post.excerpt && (
                  <p style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>{post.excerpt}</p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-primary" onClick={() => startEdit(post)}>Edit</button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setDeleteConfirmId(post.id!)}
                  >
                    Delete
                  </button>
                </div>
                {deleteConfirmId === post.id && (
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                    <p style={{ marginBottom: '0.5rem' }}>Are you sure you want to delete this post?</p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDelete(post.id!)}
                      >
                        Yes, Delete
                      </button>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {posts.length === 0 && (
              <div className="card">
                <p>No blog posts yet. Create your first post above!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManager;

